import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { NAV, LOGOUT, INVITE } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sparkles, LogOut, LayoutDashboard, ShieldCheck, UserCircle, Sun, Moon, Gift } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { MobileTopRightCta } from '@/components/MobileBottomNav';

const linkClass = ({ isActive }) =>
  `text-sm font-medium tracking-wide transition-colors ${
    isActive ? 'text-white' : 'text-slate-400 hover:text-white'
  }`;

const ThemeToggle = ({ testId }) => {
  const { toggle: toggleTheme, isDark } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid={testId}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      className="relative h-9 w-9 rounded-full glass border border-white/10 flex items-center justify-center hover:border-fuchsia-400/40 transition group"
    >
      <Sun className={`h-4 w-4 text-amber-300 absolute transition-all duration-500 ${isDark ? 'opacity-0 scale-50 rotate-180' : 'opacity-100 scale-100 rotate-0'}`} />
      <Moon className={`h-4 w-4 text-fuchsia-200 absolute transition-all duration-500 ${isDark ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 -rotate-180'}`} />
    </button>
  );
};

const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const initials = (user?.name || 'U')
    .split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={NAV.avatar}
          className="flex items-center gap-2 rounded-full glass px-2 py-1 hover:border-fuchsia-400/40 transition"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-200 pr-2 max-w-[120px] truncate">{user.name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="glass-strong border-white/10 text-slate-200 mt-2 min-w-[200px]">
        <DropdownMenuLabel className="text-slate-400 text-xs uppercase tracking-wide">
          {user.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={() => navigate('/dashboard')}
          data-testid={NAV.dashboard}
          className="cursor-pointer focus:bg-white/10"
        >
          <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/onboarding')} className="cursor-pointer focus:bg-white/10">
          <UserCircle className="h-4 w-4 mr-2" /> Artist Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/post/casting')} className="cursor-pointer focus:bg-white/10">
          <Sparkles className="h-4 w-4 mr-2" /> Post Casting
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/post/event')} className="cursor-pointer focus:bg-white/10">
          <Sparkles className="h-4 w-4 mr-2" /> Post Event
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/kyc')} className="cursor-pointer focus:bg-white/10">
          <ShieldCheck className="h-4 w-4 mr-2" /> KYC Verification
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/invite')}
          data-testid={INVITE.navMenuItem}
          className="cursor-pointer focus:bg-white/10"
        >
          <Gift className="h-4 w-4 mr-2 text-fuchsia-300" /> Invite &amp; Earn
        </DropdownMenuItem>
        {user.role === 'admin' && (
          <DropdownMenuItem
            onClick={() => navigate('/admin')}
            data-testid={NAV.admin}
            className="cursor-pointer focus:bg-white/10"
          >
            <ShieldCheck className="h-4 w-4 mr-2" /> Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={onLogout}
          data-testid={LOGOUT.button}
          className="cursor-pointer focus:bg-white/10 text-rose-300"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 inset-x-0 z-50 glass-strong border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 sm:py-3 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
          <img
            src="/maarivil-logo.png"
            alt="Maarivil — Connect, Create, Celebrate"
            data-testid="navbar-logo-image"
            className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_0_18px_rgba(217,70,239,0.35)]"
          />
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <NavLink to="/" end className={linkClass} data-testid={NAV.home}>Discover</NavLink>
          <NavLink to="/artists" className={linkClass} data-testid={NAV.artists}>Talents</NavLink>
          <NavLink to="/events" className={linkClass} data-testid={NAV.events}>Events</NavLink>
          <NavLink to="/castings" className={linkClass} data-testid={NAV.castings}>Castings</NavLink>
          {user && (
            <NavLink to="/onboarding" className={linkClass} data-testid={NAV.onboarding}>
              Become an Artist
            </NavLink>
          )}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle testId="theme-toggle-button" />
          {!user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                data-testid={NAV.login}
                className="text-slate-200 hover:text-white hover:bg-white/5"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate('/register')}
                data-testid={NAV.register}
                className="btn-rainbow text-white border-0 rounded-full px-5"
              >
                Join Maarivil
              </Button>
            </>
          ) : (
            <UserMenu user={user} onLogout={handleLogout} />
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle testId="theme-toggle-button-mobile" />
          <MobileTopRightCta />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
